import React from 'react';
import { useTranslation } from 'localization';
import { PublisherSocials } from 'types/publisher';
import './SocialLinksMolecule.scss';

export interface SocialLinksMoleculeProps {
  website?: string | null;
  socials?: PublisherSocials | null;
  className?: string;
}

const SOCIAL_CONFIG: Array<{
  key: keyof PublisherSocials;
  icon: string;
  buildUrl: (handle: string) => string;
  ariaKey: string;
}> = [
  {
    key: 'linkedin',
    icon: 'pi-linkedin',
    buildUrl: (h) => `https://linkedin.com/in/${h}`,
    ariaKey: 'marketplace.publishers.social.linkedin',
  },
  {
    key: 'twitter',
    icon: 'pi-twitter',
    buildUrl: (h) => `https://x.com/${h}`,
    ariaKey: 'marketplace.publishers.social.twitter',
  },
  {
    key: 'github',
    icon: 'pi-github',
    buildUrl: (h) => `https://github.com/${h}`,
    ariaKey: 'marketplace.publishers.social.github',
  },
  {
    key: 'youtube',
    icon: 'pi-youtube',
    buildUrl: (h) => `https://youtube.com/@${h}`,
    ariaKey: 'marketplace.publishers.social.youtube',
  },
];

const SocialLinksMolecule: React.FC<SocialLinksMoleculeProps> = ({
  website,
  socials,
  className = '',
}) => {
  const { t } = useTranslation();

  const hasAnyLink =
    website ||
    SOCIAL_CONFIG.some(({ key }) => socials?.[key]);

  if (!hasAnyLink) return null;

  const classes = ['marketplace-social-links', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {website && (
        <a
          href={website.startsWith('http') ? website : `https://${website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="marketplace-social-links__item"
          aria-label={t('marketplace.publishers.social.website')}
          title={website}
        >
          <i className="pi pi-globe" aria-hidden="true" />
        </a>
      )}
      {SOCIAL_CONFIG.map(({ key, icon, buildUrl, ariaKey }) => {
        const handle = socials?.[key];
        if (!handle) return null;
        return (
          <a
            key={key}
            href={buildUrl(handle)}
            target="_blank"
            rel="noopener noreferrer"
            className="marketplace-social-links__item"
            aria-label={t(ariaKey, { handle })}
            title={handle}
          >
            <i className={`pi ${icon}`} aria-hidden="true" />
          </a>
        );
      })}
    </div>
  );
};

SocialLinksMolecule.displayName = 'SocialLinksMolecule';

export default SocialLinksMolecule;
